import { useEffect, type ReactElement, useRef, useState } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { getErrorMessage } from '#app/utils/misc'
import { Alert, AlertTitle, AlertDescription } from './ui/alert'
import { Button } from './ui/button'

// BUILD_PHASE is build-time only, always false in browser runtime
const IS_BUILD_PHASE = typeof process !== 'undefined' && process.env?.BUILD_PHASE === 'true'

// Conditionally import Sentry only when not in build phase (lazy loaded)
let captureException: any = null

// Initialize Sentry lazily to avoid top-level await blocking module initialization
if (!IS_BUILD_PHASE && typeof window !== 'undefined') {
	import('@sentry/react').then((sentryModule) => {
		captureException = sentryModule.captureException
	}).catch(() => {
		console.warn('Sentry not available in app/components/error-boundary.tsx')
	})
}

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = (error) => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)
	const location = typeof window !== 'undefined' ? window.location : undefined
	const alertRef = useRef<HTMLDivElement>(null)
	const [countdown, setCountdown] = useState(3)
	const [autoReloading, setAutoReloading] = useState(false)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (alertRef.current) {
			alertRef.current.focus()
		}
	}, [])

	useEffect(() => {
		if (isResponse) return

		if (captureException) {
			captureException(error)
		}

		const errorMessage = getErrorMessage(error)
		if (
			errorMessage &&
			errorMessage.includes('Unable to decode turbo-stream response') &&
			location
		) {
			setAutoReloading(true)
			let seconds = 3
			setCountdown(seconds)
			const interval = setInterval(() => {
				seconds--
				setCountdown(seconds)
			}, 1000)
			const timeout = setTimeout(() => {
				location.reload()
			}, 3000)
			return () => {
				clearTimeout(timeout)
				clearInterval(interval)
			}
		} else {
			setAutoReloading(false)
		}
	}, [error, isResponse, location])

	const handleRetry = () => {
		if (location) location.reload()
	}

	const errorMessageToDisplay = isResponse
		? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
				error,
				params,
			})
		: unexpectedErrorHandler(error)

	return (
		<div className="container flex items-center justify-center break-words p-4">
			<Alert
				ref={alertRef}
				tabIndex={-1}
				role="alert"
				className="flex w-full max-w-xl flex-col items-center gap-4 p-8"
			>
				<AlertTitle className="text-xl font-bold">
					Something went wrong
				</AlertTitle>
				<AlertDescription className="mb-2">
					{errorMessageToDisplay}
				</AlertDescription>
				{autoReloading ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
								fill="none"
							/>
							<path
								className="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8v8z"
							/>
						</svg>
						Reloading in {countdown}s...
					</div>
				) : (
					<Button
						variant="outline"
						onClick={handleRetry}
						aria-label="Retry page load"
					>
						Retry
					</Button>
				)}
			</Alert>
		</div>
	)
}
